import React from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";

/**
 * Minimal, self-contained fallback for an uncaught render error. It deliberately does NOT use
 * Layout/providers (those may be the source of the crash) — only the design system + i18n,
 * which are initialised globally.
 */
const ErrorBoundaryFallback: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center dark:bg-secondary-950"
    >
      <Typography
        as="h1"
        size="2xl"
        className="text-secondary-700 dark:text-white"
      >
        {t("ErrorBoundary.title")}
      </Typography>
      <Typography
        size="lg"
        className="max-w-md text-pretty text-tertiary-700 dark:text-tertiary-100"
      >
        {t("ErrorBoundary.message")}
      </Typography>
      <Button
        id="error-boundary-reload"
        size="lg"
        variant="primary"
        onClick={() => globalThis.location.reload()}
      >
        {t("ErrorBoundary.reload")}
      </Button>
    </div>
  );
};

/**
 * App-wide error boundary. Catches uncaught render errors (which otherwise white-screen the
 * app, since there is no other boundary), reports them to Sentry, and shows a recoverable
 * fallback instead of a blank page. Inside the Storyblok Visual Editor this keeps editors from
 * facing an unexplained blank iframe when a block or draft triggers a render error.
 */
export const AppErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
    {children}
  </Sentry.ErrorBoundary>
);
